/** Best arrangement of photos on a 4x6 in sheet: tries portrait and landscape, no gaps (cut along the lines). */
export function sheetLayout(photoWmm: number, photoHmm: number) {
  const opts = [
    { sw: 101.6, sh: 152.4 },
    { sw: 152.4, sh: 101.6 },
  ].map(({ sw, sh }) => {
    const cols = Math.max(1, Math.floor((sw + 0.01) / photoWmm));
    const rows = Math.max(1, Math.floor((sh + 0.01) / photoHmm));
    return { sw, sh, cols, rows, count: cols * rows };
  });
  return opts[1].count > opts[0].count ? opts[1] : opts[0];
}

/** Head top, head centre and an estimated chin from a cut-out (alpha) image. Approximate by design. */
export function headFromAlpha(alpha: Uint8ClampedArray, w: number, h: number) {
  const rowWidth: number[] = [];
  const rowMid: number[] = [];
  for (let y = 0; y < h; y++) {
    let first = -1, last = -1, count = 0;
    for (let x = 0; x < w; x++) if (alpha[(y * w + x) * 4 + 3] > 128) { if (first < 0) first = x; last = x; count++; }
    rowWidth.push(count);
    rowMid.push(first < 0 ? -1 : (first + last) / 2);
  }
  const minRow = Math.max(3, w * 0.02);
  const top = rowWidth.findIndex((c) => c >= minRow);
  if (top < 0) return null;
  // The head is the first plateau of widths; shoulders start where the width grows well past it.
  const probe = Math.min(h - 1, top + Math.round(h * 0.12));
  const headW = Math.max(...rowWidth.slice(top, probe + 1));
  let shoulder = -1;
  for (let y = probe; y < h; y++) if (rowWidth[y] > headW * 1.7) { shoulder = y; break; }
  const mids = rowMid.slice(top, probe + 1).filter((m) => m >= 0);
  const centerX = mids.length ? mids.reduce((a, b) => a + b, 0) / mids.length : w / 2;
  const headH = shoulder > 0 ? (shoulder - top) * 0.82 : null;
  return { top, centerX, headH };
}


/** Size in source pixels of the area the crop reads from the photo (same maths as the preview and download). */
export function cropSourceSize(srcW: number, srcH: number, targetW: number, targetH: number, zoomPercent: number): { w: number; h: number } {
  const targetRatio = targetW / targetH;
  const baseH = srcW / srcH > targetRatio ? srcH : srcW / targetRatio;
  const baseW = baseH * targetRatio;
  const scale = Math.max(1, zoomPercent / 100);
  return { w: baseW / scale, h: baseH / scale };
}

/**
 * Low-resolution check: the finished photo is targetW px wide at 300 dpi. If the part of the original that
 * fills it has fewer pixels than this share of that, the print will be soft (agencies reject blurry photos).
 */
export const LOW_RES_SHARE = 0.75;
export function lowResolution(cropW: number, targetW: number): { low: boolean; share: number } {
  const share = targetW > 0 ? cropW / targetW : 1;
  return { low: share < LOW_RES_SHARE, share };
}
